import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { Chart, ChartDataset, registerables } from "chart.js";
import { ChartHelpers } from "src/app/helpers/chartHelpers";
import { DateHelpers } from "src/app/helpers/dateHelpers";
import { NumberOfMonths } from "src/app/models/charts/by-request-type/numberOfMonths.enum";
import { WorkflowStates } from "src/app/models/requests/enums/WorkflowStates.enum";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { WorkflowRequestService } from "src/app/services/workflow/WorkflowRequestService";

@Component({
    selector: 'by-request-status',
    templateUrl: './by-request-status.component.html'
})

export class ByRequestStatusComponent implements OnInit, OnChanges {
    @Input() requestStatus: WorkflowStates = WorkflowStates.Completed;
    @Input() useWorkflowRequests: boolean = false;
    @Input() workflowRequests: IWorkflowRequest[] | undefined = undefined;
    @Input() allowRequestStatusChange: boolean = false;
    @Input() showDetails: boolean = false;
    @Input() barColor: string = '';
    @Input() numberOfMonths: NumberOfMonths = NumberOfMonths.LastThreeMonths;
    chartId: string = `chart-${Math.random().toString(36).substring(2, 15)}`;
    chart: Chart;
    isLoading: boolean = true;
    requestResults: IWorkflowRequest[] = [];

    readonly numberOfMonthsOptions = Object.keys(NumberOfMonths)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
            displayName: key,
            value: NumberOfMonths[key as keyof typeof NumberOfMonths],
        }))

    readonly workflowRequestStateKeys = Object.keys(WorkflowStates)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
            displayName: key === WorkflowStates.None ? 'All' : key,
            value: WorkflowStates[key as keyof typeof WorkflowStates],
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

    constructor(private router: Router, private workflowRequestService: WorkflowRequestService) {
        this.setColor();
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.barColor && changes.barColor.currentValue) {
            this.barColor = changes.barColor.currentValue;
        }

        if (changes.useWorkflowRequests && changes.useWorkflowRequests.currentValue) {
            this.useWorkflowRequests = changes.useWorkflowRequests.currentValue;
        }

        if (changes.workflowRequests && changes.workflowRequests.currentValue) {
            this.requestResults = changes.workflowRequests.currentValue;
        }

        if (changes.requestStatus) {
            this.requestStatus = changes.requestStatus.currentValue;
            this.setColor();
        }

        if (this.useWorkflowRequests && this.chartId && this.requestResults) {
            this.setupChart();
        }
    }

    setColor() {
        this.barColor = ChartHelpers.getBrandColorByWorkflowState(this.requestStatus)
    }
    ngOnInit(): void {
        Chart.register(...registerables);
        if (!this.useWorkflowRequests) {
            this.getWorkflowRequestData();
        }
        else {
            //we show the placeholder until we get the passed data loaded into the chart
            this.isLoading = true;
        }
    }
    getWorkflowRequestData() {
        this.isLoading = true;
        this.requestResults = [];
        this.workflowRequestService.GetByState(this.requestStatus, this.numberOfMonths).subscribe({
            next: (response) => {
                this.requestResults = response.data;
                this.setupChart();
            }
        });
    }

    selected(request: IWorkflowRequest) {
        this.router.navigate([`/requests/${request.type}/${request.id}`]);
    }

    loadChartData() {
        let nowdate = DateHelpers.newFirstOfMonthDate(0);
        let monthsBackDates: Date[] = [];
        let monthsBackDatesLabel: string[] = [];
        let monthsBackData: number[] = [];

        for (let i = this.numberOfMonths; i > 0; i--) {
            const date = DateHelpers.newFirstOfMonthDate(i);
            monthsBackDates.push(date);
            monthsBackDatesLabel.push(DateHelpers.monthNames[date.getMonth()]);
        }

        let filteredData = this.requestResults.filter(x => DateHelpers.ensureDate(x.createDate) >= DateHelpers.ensureDate(monthsBackDates[0]) && DateHelpers.ensureDate(x.createDate) < DateHelpers.ensureDate(nowdate));
        let chartData = ChartHelpers.groupByAndCount(filteredData, (item) => DateHelpers.ensureDate(item.createDate).getMonth());


        for (let i = 0; i < this.numberOfMonths; i++) {
            monthsBackData.push(chartData.get(monthsBackDates[i].getMonth()) ?? 0);
        }

        var dataSets: ChartDataset<'bar', number[]>[] = [{
            label: `# of ${this.requestStatus == WorkflowStates.None ? "All" : this.requestStatus}`,
            data: monthsBackData,
            backgroundColor: this.barColor,
        }];

        if (this.requestStatus == WorkflowStates.None) {
            let allStatusDataSets: ChartDataset<'bar', number[]>[] = [];

            // If we are showing all states, we need to create a dataset for each state
            this.workflowRequestStateKeys.filter(x => x.value != WorkflowStates.None).forEach((state) => {
                const filterDataByState = filteredData.filter(w => w.state === state.value);
                const monthStats = ChartHelpers.groupByAndCount(filterDataByState, (item) => DateHelpers.ensureDate(item.createDate).getMonth());
                let stateMonthsBackData: number[] = [];
                for (let i = 0; i < this.numberOfMonths; i++) {
                    stateMonthsBackData.push(monthStats.get(monthsBackDates[i].getMonth()) ?? 0);
                }
                allStatusDataSets.push({
                    label: `${state.displayName}`,
                    data: stateMonthsBackData,
                    backgroundColor: ChartHelpers.getBrandColorByWorkflowState(state.value),
                });
            });
            dataSets = allStatusDataSets;
        }

        return {
            labels: monthsBackDatesLabel,
            datasets: dataSets
        };

    }
    setupChart() {
        if (!this.chartId) {
            return;
        }
        this.isLoading = true;
        try {
            this.chart?.destroy();

            const chartOptionsScales = this.requestStatus == WorkflowStates.None ? {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true
                }
            }
                :
                {
                    y: {
                        type: 'linear' as const, // magic
                        ticks: {
                            precision: 0
                        },
                        beginAtZero: true
                    }
                }

            let data = this.loadChartData();
            this.chart = new Chart(this.chartId, {
                type: 'bar',
                data: data,
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    scales: chartOptionsScales,
                },
            });
        }
        catch (e) {
        }
        this.isLoading = false;
    }
    onChangeRequestStatus(event: any) {
        this.requestStatus = event.value;
        this.setColor();
        this.getWorkflowRequestData();
    }
    onChangeMonthsBack(event: any) {
        this.numberOfMonths = event.value;
        this.getWorkflowRequestData();
    }
}